import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { Edit2, Trash2, Building2, User, RefreshCw } from 'lucide-react';

// Types matching backend DTOs
interface PortfolioDTO {
  id: number;
  portfolioName: string;
  portfolioCode: string;
  accountNumber: string;
  accountType: string;
  description: string;
  householdId: number;
  clientId: number;
  currentValue: number;
  status: string;
}

interface HouseholdDTO {
  id: number;
  householdName: string;
}

interface ClientDTO {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
}

interface Account {
  id: number;
  accountNumber: string;
  accountName: string;
  accountType: string;
  custodian: string;
  aum: number;
  status: string;
  modelPortfolio?: string;
  householdId: number;
  householdName: string;
  clientId?: number;
  clientName?: string;
}

const Container = styled.div<{ embedded?: boolean }>`
  padding: ${props => props.embedded ? '1.5rem' : '2rem'};
  background: ${props => props.embedded ? 'transparent' : 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)'};
  min-height: ${props => props.embedded ? 'auto' : 'calc(100vh - 64px)'};
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
`;

const Title = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' | 'ghost' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    &:hover {
      background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
      transform: translateY(-1px);
    }
  ` : props.variant === 'danger' ? `
    background: rgba(239, 68, 68, 0.1);
    color: #ef4444;
    border: 1px solid rgba(239, 68, 68, 0.3);
    &:hover {
      background: rgba(239, 68, 68, 0.2);
    }
  ` : props.variant === 'ghost' ? `
    background: transparent;
    color: #94a3b8;
    border: none;
    padding: 0.5rem;
    &:hover {
      color: #e2e8f0;
      background: rgba(255, 255, 255, 0.05);
    }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover {
      background: rgba(255, 255, 255, 0.15);
    }
  `}
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #ffffff;
  margin-bottom: 0.25rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #94a3b8;
`;

const SearchBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SearchInput = styled.input`
  flex: 1;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;

  &::placeholder {
    color: #64748b;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Select = styled.select`
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
`;

const Th = styled.th`
  text-align: left;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.05);
  color: #94a3b8;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Td = styled.td`
  padding: 1rem;
  color: #e2e8f0;
  font-size: 0.875rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
`;

const Tr = styled.tr`
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #94a3b8;
  gap: 0.5rem;
`;

const AccountTypeIcon = styled.div<{ type: string }>`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;

  ${props => {
    const type = props.type?.toUpperCase() || '';
    if (type.includes('IRA') && !type.includes('ROTH')) {
      return 'background: rgba(59, 130, 246, 0.2); color: #3b82f6;';
    } else if (type.includes('ROTH')) {
      return 'background: rgba(168, 85, 247, 0.2); color: #a855f7;';
    } else if (type.includes('TRUST')) {
      return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
    } else if (type.includes('INDIVIDUAL') || type.includes('TAXABLE')) {
      return 'background: rgba(234, 179, 8, 0.2); color: #eab308;';
    } else if (type.includes('JOINT')) {
      return 'background: rgba(236, 72, 153, 0.2); color: #ec4899;';
    } else if (type.includes('401K')) {
      return 'background: rgba(249, 115, 22, 0.2); color: #f97316;';
    } else if (type.includes('529')) {
      return 'background: rgba(14, 165, 233, 0.2); color: #0ea5e9;';
    } else {
      return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const AccountInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AccountDetails = styled.div``;

const AccountName = styled.div`
  font-weight: 600;
  color: #ffffff;
`;

const AccountNumber = styled.div`
  font-size: 0.75rem;
  color: #64748b;
  font-family: 'SF Mono', monospace;
`;

const OwnerInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const HouseholdLink = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #3b82f6;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    color: #60a5fa;
  }
`;

const ClientInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #94a3b8;
`;

const StatusBadge = styled.span<{ status: string }>`
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;

  ${props => {
    const status = props.status?.toUpperCase() || '';
    if (status === 'ACTIVE') {
      return 'background: rgba(34, 197, 94, 0.2); color: #22c55e;';
    } else if (status === 'INACTIVE') {
      return 'background: rgba(239, 68, 68, 0.2); color: #ef4444;';
    } else if (status === 'PENDING') {
      return 'background: rgba(59, 130, 246, 0.2); color: #3b82f6;';
    } else {
      return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8;';
    }
  }}
`;

const ActionCell = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-end;
`;

const ActionButton = styled.button<{ variant?: 'edit' | 'delete' }>`
  padding: 0.5rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: ${props => props.variant === 'delete' ? 'rgba(239, 68, 68, 0.1)' : 'rgba(59, 130, 246, 0.1)'};
  color: ${props => props.variant === 'delete' ? '#ef4444' : '#3b82f6'};

  &:hover {
    background: ${props => props.variant === 'delete' ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)'};
  }
`;

const Modal = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border-radius: 16px;
  padding: 2rem;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 1.5rem;
  cursor: pointer;

  &:hover {
    color: #ffffff;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #94a3b8;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  color: #ffffff;
  font-size: 0.875rem;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const ModelTag = styled.span`
  padding: 0.25rem 0.5rem;
  background: rgba(139, 92, 246, 0.2);
  color: #a78bfa;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #64748b;
`;

const formatCurrency = (value: number | null | undefined) => {
  if (value === null || value === undefined) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const getAccountTypeLabel = (type: string | null | undefined) => {
  if (!type) return '?';
  const upperType = type.toUpperCase();
  if (upperType.includes('ROTH')) return 'Roth';
  if (upperType.includes('IRA')) return 'IRA';
  if (upperType.includes('TRUST')) return 'Trust';
  if (upperType.includes('INDIVIDUAL') || upperType.includes('TAXABLE')) return 'Tax';
  if (upperType.includes('JOINT')) return 'Joint';
  if (upperType.includes('401K')) return '401k';
  if (upperType.includes('529')) return '529';
  if (upperType.includes('CUSTODIAL') || upperType.includes('UTMA')) return 'UTMA';
  if (upperType.includes('CHARITABLE')) return 'Char';
  return type.substring(0, 4);
};

interface AccountsScreenProps {
  embedded?: boolean;
  triggerNew?: number;
}

const AccountsScreen: React.FC<AccountsScreenProps> = ({ embedded = false, triggerNew = 0 }) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [typeFilter, setTypeFilter] = useState('ALL');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [households, setHouseholds] = useState<Map<number, string>>(new Map());
  const [clients, setClients] = useState<Map<number, string>>(new Map());
  const [formData, setFormData] = useState({
    portfolioName: '',
    accountNumber: '',
    accountType: 'INDIVIDUAL',
    householdId: 0,
    status: 'ACTIVE',
  });

  // Fetch all data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch portfolios, households, and clients in parallel
      const [portfoliosRes, householdsRes, clientsRes] = await Promise.all([
        axios.get<{ content: PortfolioDTO[] }>('/api/dimensions/portfolios'),
        axios.get<HouseholdDTO[]>('/api/advisor/households/all'),
        axios.get<ClientDTO[]>('/api/advisor/clients/all'),
      ]);

      // Create lookup maps
      const householdMap = new Map<number, string>();
      householdsRes.data.forEach(h => householdMap.set(h.id, h.householdName));
      setHouseholds(householdMap);

      const clientMap = new Map<number, string>();
      clientsRes.data.forEach(c => clientMap.set(c.id, c.fullName || `${c.firstName} ${c.lastName}`));
      setClients(clientMap);

      // Map portfolios to accounts
      const portfolios = portfoliosRes.data.content || portfoliosRes.data;
      const mappedAccounts: Account[] = (Array.isArray(portfolios) ? portfolios : []).map((p: PortfolioDTO) => ({
        id: p.id,
        accountNumber: p.accountNumber || p.portfolioCode || '',
        accountName: p.portfolioName,
        accountType: p.accountType || 'INDIVIDUAL',
        custodian: 'Schwab', // Default, would come from custodian link
        aum: p.currentValue || 0,
        status: p.status || 'ACTIVE',
        modelPortfolio: p.description,
        householdId: p.householdId,
        householdName: householdMap.get(p.householdId) || 'Unknown',
        clientId: p.clientId,
        clientName: p.clientId ? clientMap.get(p.clientId) : undefined,
      }));

      setAccounts(mappedAccounts);
    } catch (error) {
      console.error('Failed to fetch accounts:', error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (triggerNew > 0) {
      setSelectedAccount(null);
      setFormData({
        portfolioName: '',
        accountNumber: '',
        accountType: 'INDIVIDUAL',
        householdId: 0,
        status: 'ACTIVE',
      });
      setIsModalOpen(true);
    }
  }, [triggerNew]);

  const filteredAccounts = accounts.filter(a => {
    const matchesSearch = a.accountName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         a.accountNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         a.householdName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (a.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
    const matchesStatus = statusFilter === 'ALL' || a.status === statusFilter;
    const matchesType = typeFilter === 'ALL' || a.accountType.toUpperCase().includes(typeFilter.toUpperCase());
    return matchesSearch && matchesStatus && matchesType;
  });

  const totalAum = accounts.reduce((sum, a) => sum + (a.aum || 0), 0);
  const activeCount = accounts.filter(a => a.status === 'ACTIVE').length;
  const avgAccountAum = accounts.length > 0 ? totalAum / accounts.length : 0;

  const handleEdit = (account: Account) => {
    setSelectedAccount(account);
    setFormData({
      portfolioName: account.accountName,
      accountNumber: account.accountNumber,
      accountType: account.accountType,
      householdId: account.householdId,
      status: account.status,
    });
    setIsModalOpen(true);
  };

  const handleDelete = async (account: Account) => {
    if (!window.confirm(`Are you sure you want to delete "${account.accountName}"?`)) return;

    try {
      await axios.delete(`/api/dimensions/portfolios/${account.id}`);
      setAccounts(prev => prev.filter(a => a.id !== account.id));
    } catch (error) {
      console.error('Failed to delete account:', error);
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        portfolioName: formData.portfolioName,
        portfolioCode: formData.accountNumber,
        accountNumber: formData.accountNumber,
        accountType: formData.accountType,
        householdId: formData.householdId,
        status: formData.status,
      };

      if (selectedAccount) {
        await axios.put(`/api/dimensions/portfolios/${selectedAccount.id}`, payload);
      } else {
        await axios.post('/api/dimensions/portfolios', payload);
      }
      setIsModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Failed to save account:', error);
    }
  };

  if (loading) {
    return (
      <Container embedded={embedded}>
        <LoadingSpinner>
          <RefreshCw className="w-5 h-5 animate-spin" />
          Loading accounts...
        </LoadingSpinner>
      </Container>
    );
  }

  return (
    <Container embedded={embedded}>
      {!embedded && (
        <Header>
          <Title>All Accounts</Title>
          <HeaderActions>
            <Button variant="secondary" onClick={fetchData}>
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
            <Button variant="primary" onClick={() => {
              setSelectedAccount(null);
              setFormData({ portfolioName: '', accountNumber: '', accountType: 'INDIVIDUAL', householdId: 0, status: 'ACTIVE' });
              setIsModalOpen(true);
            }}>
              + New Account
            </Button>
          </HeaderActions>
        </Header>
      )}

      <StatsGrid>
        <StatCard>
          <StatValue>{accounts.length}</StatValue>
          <StatLabel>Total Accounts</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(totalAum)}</StatValue>
          <StatLabel>Total AUM</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{activeCount}</StatValue>
          <StatLabel>Active Accounts</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{formatCurrency(avgAccountAum)}</StatValue>
          <StatLabel>Average Account Size</StatLabel>
        </StatCard>
      </StatsGrid>

      <SearchBar>
        <SearchInput
          type="text"
          placeholder="Search accounts, households, or clients..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <Select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="ALL">All Types</option>
          <option value="IRA">IRA</option>
          <option value="ROTH">Roth IRA</option>
          <option value="INDIVIDUAL">Taxable</option>
          <option value="TRUST">Trust</option>
          <option value="JOINT">Joint</option>
          <option value="401K">401(k)</option>
          <option value="529">529</option>
        </Select>
        <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="ALL">All Status</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="PENDING">Pending</option>
        </Select>
      </SearchBar>

      {filteredAccounts.length === 0 ? (
        <EmptyState>
          <p>No accounts found</p>
          <p>Create a new account to get started</p>
        </EmptyState>
      ) : (
        <Table>
          <thead>
            <tr>
              <Th>Account</Th>
              <Th>Type</Th>
              <Th>Household / Client</Th>
              <Th>Model</Th>
              <Th>AUM</Th>
              <Th>Status</Th>
              <Th style={{ textAlign: 'right' }}>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.map((account) => (
              <Tr key={account.id}>
                <Td>
                  <AccountInfo>
                    <AccountTypeIcon type={account.accountType}>
                      {getAccountTypeLabel(account.accountType)}
                    </AccountTypeIcon>
                    <AccountDetails>
                      <AccountName>{account.accountName}</AccountName>
                      <AccountNumber>{account.accountNumber}</AccountNumber>
                    </AccountDetails>
                  </AccountInfo>
                </Td>
                <Td>{account.accountType?.replace('_', ' ') || 'Unknown'}</Td>
                <Td>
                  <OwnerInfo>
                    <HouseholdLink>
                      <Building2 className="w-3.5 h-3.5" />
                      {account.householdName}
                    </HouseholdLink>
                    {account.clientName && (
                      <ClientInfo>
                        <User className="w-3 h-3" />
                        {account.clientName}
                      </ClientInfo>
                    )}
                  </OwnerInfo>
                </Td>
                <Td>
                  {account.modelPortfolio ? (
                    <ModelTag>{account.modelPortfolio}</ModelTag>
                  ) : (
                    <span style={{ color: '#64748b' }}>-</span>
                  )}
                </Td>
                <Td style={{ fontFamily: 'SF Mono, monospace', fontWeight: 600 }}>
                  {formatCurrency(account.aum)}
                </Td>
                <Td>
                  <StatusBadge status={account.status}>{account.status}</StatusBadge>
                </Td>
                <Td>
                  <ActionCell>
                    <ActionButton variant="edit" onClick={() => handleEdit(account)}>
                      <Edit2 className="w-4 h-4" />
                    </ActionButton>
                    <ActionButton variant="delete" onClick={() => handleDelete(account)}>
                      <Trash2 className="w-4 h-4" />
                    </ActionButton>
                  </ActionCell>
                </Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      <Modal isOpen={isModalOpen}>
        <ModalContent>
          <ModalHeader>
            <ModalTitle>{selectedAccount ? 'Edit Account' : 'New Account'}</ModalTitle>
            <CloseButton onClick={() => setIsModalOpen(false)}>&times;</CloseButton>
          </ModalHeader>

          <FormRow>
            <FormGroup>
              <Label>Account Name</Label>
              <Input
                type="text"
                value={formData.portfolioName}
                onChange={(e) => setFormData({ ...formData, portfolioName: e.target.value })}
                placeholder="Account name"
              />
            </FormGroup>
            <FormGroup>
              <Label>Account Number</Label>
              <Input
                type="text"
                value={formData.accountNumber}
                onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                placeholder="Account number"
              />
            </FormGroup>
          </FormRow>

          <FormRow>
            <FormGroup>
              <Label>Account Type</Label>
              <Select
                value={formData.accountType}
                onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                style={{ width: '100%' }}
              >
                <option value="INDIVIDUAL">Taxable</option>
                <option value="IRA">IRA</option>
                <option value="ROTH">Roth IRA</option>
                <option value="TRUST">Trust</option>
                <option value="JOINT">Joint</option>
                <option value="401K">401(k)</option>
                <option value="529">529</option>
              </Select>
            </FormGroup>
            <FormGroup>
              <Label>Household</Label>
              <Select
                value={formData.householdId}
                onChange={(e) => setFormData({ ...formData, householdId: Number(e.target.value) })}
                style={{ width: '100%' }}
              >
                <option value={0}>Select Household</option>
                {Array.from(households.entries()).map(([id, name]) => (
                  <option key={id} value={id}>{name}</option>
                ))}
              </Select>
            </FormGroup>
          </FormRow>

          <FormGroup>
            <Label>Status</Label>
            <Select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              style={{ width: '100%' }}
            >
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="PENDING">Pending</option>
            </Select>
          </FormGroup>

          <div style={{ display: 'flex', gap: '1rem', marginTop: '1.5rem' }}>
            <Button variant="secondary" onClick={() => setIsModalOpen(false)} style={{ flex: 1 }}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave} style={{ flex: 1 }}>
              {selectedAccount ? 'Save Changes' : 'Create Account'}
            </Button>
          </div>
        </ModalContent>
      </Modal>
    </Container>
  );
};

export default AccountsScreen;
